// src/api/features/customers/api.ts
import { request } from "@/api/client";
import { PATHS } from "@/api/constants";


import type {
  CustomerResult,
  CreateCustomerBody,
  AttachCustomerBody,
  LookupUserResult,
  UpdateCustomerBody,
  ListCustomersQuery,
  PaginatedCustomers,
  CustomerWallet,
  ToggleWalletBody,
  CustomerHoldBooking,
  ToggleHoldBookingBody,
  WalletTransactionsPage,
  CreateWalletTransactionBody,
  WalletTransactionType,
} from "./types";

/* =========================
 * Helpers
 * ========================= */

type ApiEnvelope<T> = { ok: boolean; message?: string; data: T; meta?: any };

function unwrap<T>(res: any): T {
  if (!res || typeof res !== "object") return res as T;

  if ("ok" in res && "data" in res) {
    return (res as ApiEnvelope<T>).data;
  }

  // Fallback for { success, data }
  if ("success" in res && "data" in res) {
    return (res as { success: boolean; data: T }).data;
  }

  return res as T;
}

function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();

  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.set(k, String(v));
  }

  const s = u.toString();
  return s ? `?${s}` : "";
}

/* =========================
 * URLs (constants)
 * ========================= */

const CUSTOMERS_BASE = PATHS.customers.base;

/* =========================
 * Endpoints — CORE CUSTOMERS
 * ========================= */

/** 🔹 Create customer */
export async function createCustomer(
  body: CreateCustomerBody
): Promise<CustomerResult> {
  const dto: any = { ...body };

  // Light normalization / trimming (core identity + some business fields)
  if (dto.firstName != null) dto.firstName = String(dto.firstName).trim();
  if (dto.lastName != null) dto.lastName = String(dto.lastName).trim();
  if (dto.username != null) dto.username = String(dto.username).trim();
  if (dto.email != null) dto.email = String(dto.email).trim();

  if (dto.displayName != null) dto.displayName = String(dto.displayName).trim();
  if (dto.companyName != null) dto.companyName = String(dto.companyName).trim();
  if (dto.customerCode != null)
    dto.customerCode = String(dto.customerCode).trim();

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[createCustomer] payload →", dto);
  }

  const res = await request<CustomerResult | ApiEnvelope<CustomerResult>>({
    method: "POST",
    url: CUSTOMERS_BASE,
    data: dto,
  });

  return unwrap<CustomerResult>(res);
}

/** 🔹 Look up whether a user account already exists for an email */
export async function lookupCustomerUser(
  email: string
): Promise<LookupUserResult> {
  const res = await request<LookupUserResult | ApiEnvelope<LookupUserResult>>({
    method: "GET",
    url: `${CUSTOMERS_BASE}/lookup-user${qs({ email })}`,
  });
  return unwrap<LookupUserResult>(res);
}

/** 🔹 Attach a customer profile to an EXISTING user (one identity, +role) */
export async function attachCustomer(
  body: AttachCustomerBody
): Promise<CustomerResult> {
  const res = await request<CustomerResult | ApiEnvelope<CustomerResult>>({
    method: "POST",
    url: `${CUSTOMERS_BASE}/attach`,
    data: body,
  });
  return unwrap<CustomerResult>(res);
}

/** 🔹 List customers (paginated) */
export async function listCustomers(
  query: ListCustomersQuery = {}
): Promise<PaginatedCustomers> {
  // Clamp + sanitize pagination
  const safePage = Math.max(1, Number(query.page ?? 1));
  const rawLimit = Number(query.limit ?? 10);
  const safeLimit = Math.max(
    1,
    Math.min(100, Number.isFinite(rawLimit) ? rawLimit : 10)
  );

  const url = `${CUSTOMERS_BASE}${qs({
    page: safePage,
    limit: safeLimit,
    q: query.q,
    status: query.status,
    type: query.type,
    marketingOptIn:
      typeof query.marketingOptIn === "boolean"
        ? query.marketingOptIn
        : undefined,
  })}`;

  const res = await request<
    PaginatedCustomers | (ApiEnvelope<CustomerResult[]> & { meta?: any })
  >({
    method: "GET",
    url,
  });

  // If server returns { ok, data, meta }
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const r = res as ApiEnvelope<CustomerResult[]> & { meta?: any };

    const items = Array.isArray(r.data) ? r.data : [];
    const meta = r.meta ?? {};

    return {
      items,
      page: Number(meta.page ?? safePage),
      pageSize: Number(meta.pageSize ?? meta.limit ?? safeLimit),
      total: Number(meta.total ?? items.length ?? 0),
    };
  }

  // Otherwise assume backend already returns PaginatedCustomers
  return res as PaginatedCustomers;
}

/** 🔹 Get customer by id */
// src/api/features/customers/api.ts
export async function getCustomer(
  id: number | string
): Promise<CustomerResult> {
  const res = await request<CustomerResult | ApiEnvelope<CustomerResult>>({
    method: "GET",
    url: `${CUSTOMERS_BASE}/${id}`,
  });

  const unwrapped = unwrap<CustomerResult>(res);

  // Force logs, no env condition
  // console.log("[getCustomer] RAW →", res);
  // console.log("[getCustomer] UNWRAPPED →", unwrapped);

  return unwrapped;
}



/** 🔹 Update customer */
export async function updateCustomer(
  id: number | string,
  body: UpdateCustomerBody
): Promise<CustomerResult> {
  const dto: any = { ...body };

  // Same light normalization as create
  if (dto.firstName != null) dto.firstName = String(dto.firstName).trim();
  if (dto.lastName != null) dto.lastName = String(dto.lastName).trim();
  if (dto.username != null) dto.username = String(dto.username).trim();
  if (dto.email != null) dto.email = String(dto.email).trim();

  if (dto.displayName != null) dto.displayName = String(dto.displayName).trim();
  if (dto.companyName != null) dto.companyName = String(dto.companyName).trim();
  if (dto.customerCode != null)
    dto.customerCode = String(dto.customerCode).trim();

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[updateCustomer] id=", id, "payload →", dto);
  }

  const res = await request<CustomerResult | ApiEnvelope<CustomerResult>>({
    method: "PUT",
    url: `${CUSTOMERS_BASE}/${id}`,
    data: dto,
  });

  return unwrap<CustomerResult>(res);
}

/** 🔹 Soft delete customer */
export async function deleteCustomer(
  id: number | string
): Promise<{ message: string }> {
  const res = await request<
    { message: string } | ApiEnvelope<{ message: string }>
  >({
    method: "DELETE",
    url: `${CUSTOMERS_BASE}/${id}`,
  });

  return unwrap<{ message: string }>(res);
}

/** 🔹 Get wallet summary for a customer */
export async function getCustomerWallet(
  id: number | string
): Promise<CustomerWallet> {
  const res = await request<CustomerWallet | ApiEnvelope<CustomerWallet>>({
    method: "GET",
    url: `${CUSTOMERS_BASE}/${id}/wallet`,
  });
  return unwrap<CustomerWallet>(res);
}

/** 🔹 Enable/Disable wallet for a customer */
export async function toggleCustomerWallet(
  id: number | string,
  body: ToggleWalletBody
): Promise<CustomerWallet> {
  const res = await request<CustomerWallet | ApiEnvelope<CustomerWallet>>({
    method: "PUT",
    url: `${CUSTOMERS_BASE}/${id}/wallet`,
    data: body,
  });
  return unwrap<CustomerWallet>(res);
}

/** 🔹 Get the flight hold permission for a customer */
export async function getCustomerHoldBooking(
  id: number | string
): Promise<CustomerHoldBooking> {
  const res = await request<CustomerHoldBooking | ApiEnvelope<CustomerHoldBooking>>({
    method: "GET",
    url: `${CUSTOMERS_BASE}/${id}/hold-booking`,
  });
  return unwrap<CustomerHoldBooking>(res);
}

/** 🔹 Grant/revoke the flight hold permission for a customer */
export async function toggleCustomerHoldBooking(
  id: number | string,
  body: ToggleHoldBookingBody
): Promise<CustomerHoldBooking> {
  const res = await request<CustomerHoldBooking | ApiEnvelope<CustomerHoldBooking>>({
    method: "PUT",
    url: `${CUSTOMERS_BASE}/${id}/hold-booking`,
    data: body,
  });
  return unwrap<CustomerHoldBooking>(res);
}

/** 🔹 Add credit/debit wallet transaction */
export async function createCustomerWalletTransaction(
  id: number | string,
  body: CreateWalletTransactionBody
): Promise<CustomerWallet> {
  const dto = {
    txnType: String(body.txnType || "CREDIT").toUpperCase() as WalletTransactionType,
    amount: Number(body.amount || 0),
    note: body.note ?? null,
    referenceType: body.referenceType ?? null,
    referenceId: body.referenceId ?? null,
    productType: body.productType ?? null,
    bookingId: body.bookingId ?? null,
  };
  const res = await request<CustomerWallet | ApiEnvelope<CustomerWallet>>({
    method: "POST",
    url: `${CUSTOMERS_BASE}/${id}/wallet/transactions`,
    data: dto,
  });
  return unwrap<CustomerWallet>(res);
}

/** 🔹 List wallet transactions */
export async function listCustomerWalletTransactions(
  id: number | string,
  query: { page?: number; limit?: number; txnType?: WalletTransactionType } = {}
): Promise<WalletTransactionsPage> {
  const safePage = Math.max(1, Number(query.page ?? 1));
  const safeLimit = Math.max(1, Math.min(100, Number(query.limit ?? 20)));
  const url = `${CUSTOMERS_BASE}/${id}/wallet/transactions${qs({
    page: safePage,
    limit: safeLimit,
    txnType: query.txnType,
  })}`;

  const res = await request<
    WalletTransactionsPage | (ApiEnvelope<any[]> & { meta?: any })
  >({
    method: "GET",
    url,
  });

  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const r = res as ApiEnvelope<any[]> & { meta?: any };
    const items = Array.isArray(r.data) ? r.data : [];
    const meta = r.meta ?? {};
    return {
      items,
      page: Number(meta.page ?? safePage),
      pageSize: Number(meta.pageSize ?? safeLimit),
      total: Number(meta.total ?? items.length),
      wallet: (meta.wallet ?? {
        customerId: Number(id),
        enabled: false,
        balance: 0,
        currency: "INR",
      }) as CustomerWallet,
    };
  }

  return res as WalletTransactionsPage;
}
